// src/components/admin/PriceListGenerator.js
import React, { useState } from 'react';
import { formatPrice, parsePrice } from '../../AppUtils.js';

const PriceListGenerator = () => {
  const [priceList, setPriceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const generatePriceList = async () => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0 });

    try {
      const response = await fetch('https://www.vesaro.com/_functions/test?limit=1');
      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      const allSimulators = data.allBaseSimulators || [];
      const validSimulators = allSimulators.filter(sim => sim.slug);

      setProgress({ current: 0, total: validSimulators.length });

      // Fetch all simulators in parallel (but in batches to not overwhelm the server)
      const batchSize = 10; // Process 10 at a time
      const results = [];

      for (let i = 0; i < validSimulators.length; i += batchSize) {
        const batch = validSimulators.slice(i, i + batchSize);
        
        const batchResults = await Promise.all(
          batch.map(async (sim) => {
            try {
              const simResponse = await fetch(`https://www.vesaro.com/_functions/test?slug=${sim.slug}`);
              if (!simResponse.ok) return null;

              const simData = await simResponse.json();
              const baseConfig = simData.baseConfig;

              if (!baseConfig) return null;

              const defaultModules = baseConfig.defaultModules || [];
              
              const total = defaultModules.reduce((sum, module) => {
                return sum + parsePrice(module.test_price || 0);
              }, 0);

              const moduleNames = defaultModules.map(module => ({
                name: module.title || module.name || 'Unknown',
                price: parsePrice(module.test_price || 0)
              }));

              return {
                name: baseConfig.productName || baseConfig['Product Name'] || sim.slug,
                slug: sim.slug,
                modules: moduleNames,
                total: total
              };
            } catch (err) {
              console.error(`Failed to process ${sim.slug}:`, err);
              return null;
            }
          })
        );

        // Filter out nulls and add to results
        results.push(...batchResults.filter(r => r !== null));
        setProgress({ current: Math.min(i + batchSize, validSimulators.length), total: validSimulators.length });
      }

      results.sort((a, b) => a.name.localeCompare(b.name));
      setPriceList(results);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    let csv = 'Simulator Name,Included Modules,Total Test Price\n';

    priceList.forEach(sim => {
      const modulesText = sim.modules.map(m => `${m.name} (${formatPrice(m.price)})`).join('; ');
      csv += `"${sim.name}","${modulesText}",${sim.total}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vesaro-test-price-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Price List Generator</h1>
        <p className="text-white/60 text-sm">Generate total test prices for all simulator configurations</p>
      </div>

      <div className="mb-6 flex gap-3">
        <button
          onClick={generatePriceList}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate Test Price List'}
        </button>

        {priceList.length > 0 && (
          <button
            onClick={downloadCSV}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            Download CSV
          </button>
        )}
      </div>

      {/* Progress indicator */}
      {loading && progress.total > 0 && (
        <div className="mb-6">
          <div className="bg-white/10 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-blue-500 h-full transition-all duration-300"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
          <p className="text-white/60 text-sm mt-2 text-center">
            Processing {progress.current} of {progress.total} simulators...
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6">
          <p className="text-red-200">Error: {error}</p>
        </div>
      )}

      {priceList.length > 0 && (
        <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/10">
                  <th className="px-4 py-3 text-left text-white font-semibold">Simulator</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Included Modules</th>
                  <th className="px-4 py-3 text-right text-white font-semibold">Total Test Price</th>
                </tr>
              </thead>
              <tbody>
                {priceList.map((sim, index) => (
                  <tr 
                    key={sim.slug}
                    className={index % 2 === 0 ? 'bg-white/5' : 'bg-transparent'}
                  >
                    <td className="px-4 py-3 text-white font-medium">{sim.name}</td>
                    <td className="px-4 py-3 text-white/80 text-sm">
                      <div className="font-medium mb-1">{sim.modules.length} modules:</div>
                      <div className="text-xs text-white/60 space-y-0.5">
                        {sim.modules.map((m, i) => (
                          <div key={i}>• {m.name} - {formatPrice(m.price)}</div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-white font-bold">
                      {formatPrice(sim.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && progress.total === 0 && (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
          <p className="text-white/60 mt-3">Loading simulator list...</p>
        </div>
      )}
    </div>
  );
};

export default PriceListGenerator;